import { getErrorMessage, logMessage } from '../utils/helpers';
import { Zlib } from '../js/zlib';
import { remoteConfigGetter } from '../utils/remoteConfig';
import { HandleSocket } from '../components/common/handleSocket';
import ReconnectingWebSocket from 'reconnecting-websocket';
import Queue from 'queue';
import Subscribed from '../models/symbolData';
import { ResolveSymbolInfo, Quotes } from '../models/symbolData';
import { LastDailyBar, OdinMessagePacket, Handlers } from '../models/charts';
import { LastBarsCache } from './bar-cache';
import { MarketDepth, Bids } from '../models/future';
import { FeedUiController } from '../components/TVChartContainer/title_sync';
const moment = require('moment');

let worker: Worker | undefined;
const channelToSubscription = new Map<string, OdinMessagePacket>();
const singleQuoteMap = new Map();
// uid -> array of channelStrings. A bar subscription on an index with a volume
// proxy spans TWO channels (the index spot channel + the nearest-future channel),
// so a single guid can map to more than one channel — hence an array (ADVA-847).
const guidToSubscription = new Map();
// Index volume/OI proxy (ADVA-847, ported from mobile_charts ADVA-846). Keyed by
// the future's channelString ("{segment}-{token}"). `proxyLatest` holds the
// values the index 'bar' handler reads instead of its own spot tick (which
// carries volume=0 / no OI). `perCandle` is the future's volume *within the
// current candle*, derived from the future stream itself (see `proxyState`) — NOT
// from the index's stored history, which is enriched server-side and may be
// absent/zero. This makes the index live volume equal the future's own chart
// volume regardless of backend enrichment. Both maps cleared in unsubscribe.
const proxyLatest = new Map<string, { cumVolume: number; perCandle: number; oi: any; oi_ch: any }>();
// Per future channel: { bucket, base }. `bucket` = current candle start (ms, at
// the chart resolution); `base` = the future's cumulative day volume at the
// candle's FIRST tick. perCandle = cumVolume - base. Using the first tick's
// cumulative (not the prior candle's last) mirrors how the future's OWN chart
// aggregates, so the index per-candle volume matches it bar-for-bar.
const proxyState = new Map<string, { bucket: number; base: number }>();

const queue = Queue({ results: [] });
queue.autostart = true;
queue.timeout = 1000;
queue.concurrency = 1;

// ----- reconnect backfill + gap-bridge ----------------------------------
// After tab inactivity / network loss, the socket reconnects but the outage
// window shows flat candles. On reconnect we refetch /history to backfill the
// gap; while that lands we hold ticks (pendingReload) so they resume on the
// real backfilled bar, and if the refetch never lands we synthesize a current-
// period bar so the chart resumes instead of freezing. Mirrors mobile_charts.
const PENDING_RELOAD_TIMEOUT_MS = 7000;
const RELOAD_THROTTLE_MS = 3000;
const VISIBILITY_RELOAD_THRESHOLD_MS = 10000;   // hidden ≥ this → backfill on return
const FORCE_RECONNECT_THRESHOLD_MS = 60000;     // hidden ≥ this → also force a fresh socket
// First socket_opened is the initial connect (TV already loaded history); every
// later open is a post-outage reconnect that must backfill the missed candles.
let hasConnectedBefore = false;
let lastReloadAt = 0;
let hiddenSince = 0;
// TV's onResetCacheNeededCallback per bar series, keyed by SYMBOL (mirrors
// mobile_charts). NOT keyed by subscriber UID: TV churns the UID on every reset/
// resubscribe, so a UID-keyed map emptied to 0 after the first reset (exactly the
// bug seen in the logs). Symbol keying stays stable, so every reset reliably fires
// the callback → TV re-requests getBars for the series → the gap backfills.
const resetCacheCallbackMap = new Map<string, () => void>();

// Synthesize a current-period bar to resume from when the /history refetch never
// lands. Period start matches the bar-builder's per-resolution calc (IST offset)
// so the next tick lands in-period and accumulates correctly.
function synthesizePostTimeoutBar(resolution: string, tradeTimeMs: number, tradePrice: number, cumulativeVolume: number): LastDailyBar {
    let periodStart: number;
    if (resolution == '1') {
        periodStart = moment(tradeTimeMs).startOf('minute').toDate().getTime();
    } else if (resolution == '1S') {
        periodStart = moment(tradeTimeMs).startOf('second').toDate().getTime();
    } else if (resolution == '1D') {
        periodStart = moment(tradeTimeMs).startOf('day').toDate().getTime() + 19800000;
    } else if (resolution == '1W') {
        periodStart = moment(tradeTimeMs).startOf('week').toDate().getTime() + 86400000 + 19800000;
    } else if (resolution == '1M') {
        periodStart = moment(tradeTimeMs).startOf('month').toDate().getTime() + 19800000;
    } else if (resolution == '1Y') {
        periodStart = moment(tradeTimeMs).startOf('year').toDate().getTime() + 19800000;
    } else {
        periodStart = moment(tradeTimeMs).startOf('minute').toDate().getTime();
    }
    const safePrice = Number.isFinite(tradePrice) ? tradePrice : 0;
    const safeCumulative = Number.isFinite(cumulativeVolume) ? cumulativeVolume : 0;
    return { time: periodStart, open: safePrice, high: safePrice, low: safePrice, close: safePrice, volume: 0, totalLastDayVolumeTillBar: safeCumulative };
}

// Kick the worker to reconnect a possibly-zombie socket (network back / tab resumed).
function forceReconnect() {
    worker?.postMessage({ type: 'force_reconnect' });
}

function onOnline() {
    logMessage('reconnect: online → backfill + force reconnect');
    reloadChartBars('online');
    forceReconnect();
}

function onVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
        return;
    }
    if (!hiddenSince) return;
    const hiddenMs = Date.now() - hiddenSince;
    hiddenSince = 0;
    if (hiddenMs > VISIBILITY_RELOAD_THRESHOLD_MS) {
        logMessage(`reconnect: tab visible after ${Math.round(hiddenMs / 1000)}s → backfill`);
        reloadChartBars('visibility');
        if (hiddenMs > FORCE_RECONNECT_THRESHOLD_MS) forceReconnect();
    }
}

// Faithful port of mobile_charts ResetAllBarsCache: drop the last-bar cache, mark
// every bar handler for re-seed (pendingReload), then FIRE every series'
// onResetCacheNeededCallback so TV re-requests /history and backfills the gap.
// The callback firing is the actual refetch trigger — this is what was missing.
function resetAllBarsCache() {
    LastBarsCache.clear();
    for (const subscriptionItem of channelToSubscription.values()) {
        if (!subscriptionItem || !subscriptionItem.handlers) continue;
        subscriptionItem.handlers.forEach((handler: Handlers) => {
            if (handler.type === 'bar') {
                handler.pendingReload = true;
                handler.pendingReloadSince = Date.now();
            }
        });
    }
    resetCacheCallbackMap.forEach((cb) => {
        try { cb(); } catch (e) { logMessage('reconnect: reset callback failed ' + getErrorMessage(e), 2); }
    });
}

// Backfill candles missed during an outage. Mirrors mobile_charts reloadChartBars:
// resetAllBarsCache() (fires every series' onResetCacheNeededCallback → TV
// re-requests /history) AND resetData() on the active chart — both, every time.
// Throttled so an online + visibility + socket-reopen burst = a single refetch.
function reloadChartBars(source: string) {
    const now = Date.now();
    if (now - lastReloadAt < RELOAD_THROTTLE_MS) { return; }
    lastReloadAt = now;
    resetAllBarsCache();
    try {
        const w: any = (typeof window !== 'undefined') ? (window as any).tvWidget : undefined;
        if (w && typeof w.activeChart === 'function' && w.activeChart()) {
            w.activeChart().resetData();
        }
    } catch (e) { logMessage('reconnect: resetData failed ' + getErrorMessage(e), 2); }
    logMessage(`reconnect: backfill(${source}) → ${resetCacheCallbackMap.size} series`);
}
// ------------------------------------------------------------------------

// Recreate the subscription map
function recreateMap() {
    for (const [channelString, subscriptionItem] of channelToSubscription.entries()) {
        const [market_segment_id, token] = channelString.split('-');
        if (subscriptionItem.isSubscribed) {
            let payload: ResolveSymbolInfo = {
                market_segment_id: Number(market_segment_id),
                token: token
            }
            if (subscriptionItem.isMarketDepth) {
                SendMessage(
                    _getSubscribeDepthRequest(payload)
                );
            } else {
                SendMessage(
                    _getSubscribeTouchRequest(payload)
                );
            }
        }
    }
}

// Check if the browser is Chrome
function isChrome(): boolean {
    return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
}

let socketUrl: string;

// Open WebSocket connection
export function openWebSocketConnection(ocToken: string, clientCode: string) {
    if (window.Worker) {
        if (worker) {
            worker.terminate();
        }

        socketUrl = remoteConfigGetter('ltp_socket_url').asString();

        worker = new Worker(`${process.env.PUBLIC_URL}/worker.js`);
        worker.postMessage({ type: 'initialize', socketUrl, ocToken, clientCode, });
        FeedUiController.setStatus('CONNECTING');
        worker.onmessage = (e: MessageEvent) => {
            if (e.data.type === 'socket_opened') {
                console.log('Worker socket opened');
                HandleSocket(true);
                recreateMap();
                // First open = initial connect (TV already has history). Every later
                // open is a reconnect after an outage — backfill the missed candles.
                if (hasConnectedBefore) {
                    logMessage('reconnect: socket reopened → backfill');
                    reloadChartBars('socket_reopen');
                } else {
                    hasConnectedBefore = true;
                }
            } else if (e.data.type === 'socket_closed') {
                HandleSocket(false);
            } else if (e.data.type === 'socket_error') {
                console.error('Worker socket error in socket_error:', e.data);
            } else if (e.data.type === 'tick') {
                ProcessPacketString(e.data.data);
            } else if (e.data.type === 'websocket_state') {
                console.log('WebSocket state changed:', e.data.state);
                FeedUiController.setStatus(e.data.state);
            } else if (e.data.type === 'warn') {
                console.warn('Worker warning:', e.data.message);
            }
        };

        worker.onmessageerror = (e: MessageEvent) => {
            console.trace('Worker error', e);
        };

        // Recover a zombie socket promptly when the OS network returns or a
        // long-frozen tab resumes (onclose may never fire, so it won't reconnect
        // on its own). The chart backfills missed candles separately, on the
        // resulting socket_opened.
        window.removeEventListener('online', onOnline);
        window.addEventListener('online', onOnline);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        document.addEventListener('visibilitychange', onVisibilityChange);
    }
}

// Close WebSocket connection
export function closeConnection() {
    logMessage('Streaming Provider: Close connection called');
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    hasConnectedBefore = false;
    if (worker) {
        worker.postMessage({ type: 'close' });
        worker.terminate();
        worker = undefined;
    }
    channelToSubscription.clear();
    singleQuoteMap.clear();
    guidToSubscription.clear();
}

// Temporarily close WebSocket connection
export function closeConnectionTemporarily() {
    logMessage('Streaming Provider: Closing connection temporarily');
    if (worker) {
        worker.postMessage({ type: 'temporary_close' });
    }
}

// Subscribe to Odin stream
export function subscribeOnOdinStream(
    symbols: ResolveSymbolInfo[],
    resolution: string | undefined,
    onRealtimeCallback: (a: any) => void,
    subscribeUID: string,
    onResetCacheNeededCallback: () => void | undefined,
    lastDailyBar: LastDailyBar | undefined,
    type: string
) {
    // console.log("lastDailyBar",lastDailyBar)
    // Store TV's "refetch now" callback keyed by SYMBOL (see resetCacheCallbackMap)
    // so a reconnect reliably re-requests /history for this series even after TV
    // churns the subscriber UID on reset.
    if (type === 'bar' && typeof onResetCacheNeededCallback === 'function') {
        symbols.forEach((s) => resetCacheCallbackMap.set(String(s.symbol), onResetCacheNeededCallback));
    }
    queue.push((cb) => {
        try {
            if (type === 'depth') {
                _subscribeDepth(symbols[0], onRealtimeCallback, subscribeUID);
            } else if (type === 'quotes' || type === 'tt-quotes') {
                _subscribeQuotes(symbols, onRealtimeCallback, subscribeUID, type);
            } else if (type === 'bar') {
                _subscribeBars(
                    symbols,
                    onRealtimeCallback,
                    subscribeUID,
                    lastDailyBar as LastDailyBar,
                    String(resolution)
                );
            } else if (type === 'single-quotes') {
                _subscribeSingleQuotes(
                    symbols,
                    onRealtimeCallback,
                    subscribeUID
                );
            }

            symbols.forEach((symbol: ResolveSymbolInfo) => {
                const channelString: string = `${symbol.odin_market_segment_id}-${symbol.odin_token}`;
                const subs: OdinMessagePacket = channelToSubscription.get(channelString) as OdinMessagePacket;
                if (subs) {
                    let needsDepth: boolean = false;
                    let needsTouchline: boolean = false;

                    subs.handlers.forEach((handler: Handlers) => {
                        if (handler.type === 'depth') {
                            needsDepth = true;
                        } else {
                            needsTouchline = true;
                        }
                    });

                    if (!subs.isSubscribed) {
                        if (needsDepth) {
                            const request = _getSubscribeDepthRequest(symbol);
                            SendMessage(request);
                            subs.isSubscribed = true;
                            subs.isMarketDepth = true;
                        } else {
                            const request = _getSubscribeTouchRequest(symbol);
                            SendMessage(request);
                            subs.isSubscribed = true;
                            subs.isTouchline = true;
                        }
                        channelToSubscription.set(channelString, subs);
                    } else {
                        if (needsDepth && !subs.isMarketDepth) {
                            let request = _getUnsubscribeTouchRequest(symbol);
                            SendMessage(request);

                            request = _getSubscribeDepthRequest(symbol);
                            SendMessage(request);

                            subs.isTouchline = false;
                            subs.isMarketDepth = true;
                            channelToSubscription.set(channelString, subs);
                        }
                        ProcessPacketString(subs.lastPacket);
                    }
                }
            });
        } catch (e) {
            console.log('Subscription error', e);
        }
        if (cb) {
            cb();
        }
    });
}


export function unsubscribe(listenerGuid: string) {
    // NOTE: resetCacheCallbackMap is keyed by symbol and intentionally NOT deleted
    // here (mirrors mobile_charts) — TV resubscribes the same symbol with a fresh
    // UID on reset, and symbol keying keeps the callback stable across that churn.
    queue.push((cb) => {
        let channels = guidToSubscription.get(listenerGuid);
        if (!channels) {
            if (cb) {
                cb();
            }
            return
        }
        // A guid can own more than one channel (index bar + its volume proxy), so
        // iterate. Older entries may still be a bare string — normalise both.
        const channelList = Array.isArray(channels) ? channels : [channels];
        channelList.forEach((channelString: any) => {
            const [market_segment_id, token] = channelString.split("-")
            let subscriptionItem = channelToSubscription.get(channelString)
            if (!subscriptionItem) {
                return
            }
            subscriptionItem.handlers = subscriptionItem.handlers.filter(function (handler: Handlers) {
                return handler.id != listenerGuid;
            })
            channelToSubscription.set(channelString, subscriptionItem)
            if (subscriptionItem.handlers) {
                if (subscriptionItem.handlers.length == 0) {
                    if (subscriptionItem["isSubscribed"]) {
                        if (subscriptionItem["isMarketDepth"]) {
                            var request = _getSubscribeDepthRequest({ odin_market_segment_id: market_segment_id, odin_token: token })
                            SendMessage(request)
                        } else {
                            var request = _getUnsubscribeTouchRequest({ odin_market_segment_id: market_segment_id, odin_token: token })
                            SendMessage(request)
                        }
                    }
                    channelToSubscription.delete(channelString)
                    // Drop the cached future volume/OI + candle state when its channel
                    // goes idle. A no-op for non-proxy channels (keys simply absent).
                    proxyLatest.delete(channelString)
                    proxyState.delete(channelString)
                } else {
                    var needsDepth = false
                    subscriptionItem.handlers.forEach((handler: Handlers) => {
                        if (handler.type == 'depth') {
                            needsDepth = true
                        }
                    })
                    if (!needsDepth) {
                        if (subscriptionItem["isMarketDepth"]) {
                            var request2 = _getUnsubscribeDepthRequest({ odin_market_segment_id: market_segment_id, odin_token: token })
                            SendMessage(request2)
                            request2 = _getSubscribeTouchRequest({ odin_market_segment_id: market_segment_id, odin_token: token })
                            SendMessage(request2)
                            subscriptionItem["isSubscribed"] = true
                            subscriptionItem["isTouchline"] = true
                            subscriptionItem["isMarketDepth"] = false
                            channelToSubscription.set(channelString, subscriptionItem)
                        }
                    }
                }
            }
        })
        guidToSubscription.delete(listenerGuid)
        if (cb) {
            cb();
        }
    })

}

//Varibales name change done
function SendMessage(odinMessage: string) {
    if (worker != null) {
        worker.postMessage({ type: 'message', message: odinMessage });
    }
}


// Exported for the ADVA-847 real-module tests (drives ticks the way the worker
// does in production); not part of the public datafeed surface.
export function ProcessPacketString(data: string) {
    let data_map: Map<string, string> = marketDepthProcessMessage(data);

    let message_code = parseInt(String(data_map.get('64')));
    if (message_code != 128 && message_code != 209) {
        return
    }

    let tradePrice = parseFloat(String(data_map.get('8')));
    let openPrice = parseFloat(String(data_map.get('75')));
    let highPrice = parseFloat(String(data_map.get('77')));
    let lowPrice = parseFloat(String(data_map.get('78')));
    let closePrice = parseFloat(String(data_map.get('76')));
    let volume = parseFloat(String(data_map.get('79')));
    let totalBuyQty = data_map.get('81');
    let totalSellQty = data_map.get('82');
    let divider = parseFloat(String(data_map.get('399')) || '100')
    let token = data_map.get('7')
    let market_segment = data_map.get('1')
    let lastTradeQuantity = parseFloat(String(data_map.get('9')));
    let dpr = data_map.get('380');
    let oi = data_map.get('88');
    let oi_ch = data_map.get('465');
    let depth: MarketDepth = data_map.get('depth') as MarketDepth
    // let buy_qty = parseFloat(  depth.asks[0]?.volume ||data_map.get('2') || "0");
    // let buy_price = 0.0
    let buy_qty: number, buy_price: number, sell_qty: number, sell_price: number = 0.0
    if (data_map.has('3') && data_map.has('5')) {
        buy_price = parseFloat(String(data_map.get('3'))) / divider
        sell_price = parseFloat(String(data_map.get('6'))) / divider
        buy_qty = parseFloat(String(data_map.get('2')))
        sell_qty = parseFloat(String(data_map.get('5')))
    } else {
        depth.asks?.forEach((row: Bids, index: number) => {
            if (depth.asks) {
                depth.asks[index].price = depth.asks[index].price / divider
            }
        })
        depth.bids?.forEach((row: Bids, index: number) => {
            if (depth.bids)
                depth.bids[index].price = depth.bids[index].price / divider
        })
        if (depth.bids && depth.asks) {
            buy_qty = depth.asks[0]?.volume
            sell_qty = depth.asks[0]?.volume
            buy_price = depth.bids[4]?.price
            sell_price = depth.asks[0]?.price
        }
    }
    var tradeTime = odinDateParse(data_map.get('74') as string);

    let channelString = market_segment + "-" + token;

    let subscriptionItem = channelToSubscription.get(channelString);
    if (!subscriptionItem) {
        return;
    }
    if (!subscriptionItem.handlers) {
        return;
    }
    subscriptionItem.lastPacket = data
    channelToSubscription.set(channelString, subscriptionItem);
    // console.log(subscriptionItem)
    subscriptionItem.handlers.forEach((handler: Handlers) => {
        if (handler.type == 'quotes') {
            let quote = {
                s: 'ok',
                n: handler.symbol,
                v: {
                    ch: (tradePrice - closePrice) / divider,
                    chp: ((tradePrice - closePrice) / closePrice * 100),
                    short_name: handler.symbol.split(":")[1],
                    exchange: handler.symbol.split(":")[0],
                    description: handler.symbol,
                    lp: tradePrice / divider,
                    open_price: openPrice / divider,
                    high_price: highPrice / divider,
                    low_price: lowPrice / divider,
                    prev_close_price: closePrice / divider,
                    volume: volume,
                    dpr: dpr,
                    token: token,
                    market_segment_id: market_segment,
                    oi: oi,
                    oi_ch: oi_ch,
                    ask: sell_price,
                    ask_qty: sell_qty,
                    bid: buy_price,
                    bid_qty: buy_qty,
                    average_trade_price: ((highPrice + lowPrice) / 2) / (divider)
                }

            }
            try {
                handler.handler.callback([quote])
            }
            catch (err) {
                console.info(err)
            }

        } else if (handler.type == 'tt-quotes') {
            let quote = {
                s: 'success',
                n: handler.symbol,
                v: {
                    ch: (tradePrice - closePrice) / divider,
                    chp: ((tradePrice - closePrice) / closePrice * 100),
                    short_name: handler.symbol.split(":")[1],
                    exchange: handler.symbol.split(":")[0],
                    description: handler.symbol,
                    lp: tradePrice / divider,
                    open_price: openPrice / divider,
                    high_price: highPrice / divider,
                    low_price: lowPrice / divider,
                    prev_close_price: closePrice / divider,
                    volume: volume,
                    dpr: dpr,
                    ask: sell_price,
                    ask_qty: sell_qty,
                    bid: buy_price,
                    bid_qty: buy_qty,
                    average_trade_price: ((highPrice + lowPrice) / 2) / (divider)
                }

            }
            try {
                handler.handler.callback(quote)
            }
            catch (err) {
                console.info(err)
            }
        } else if (handler.type == 'bar') {
            // After a reconnect (resetAllBarsCache set pendingReload): hold this
            // handler's ticks until the fresh /history lands, then reseed
            // lastDailyBar from LastBarsCache so ticks resume on the backfilled
            // history instead of the stale pre-gap bar. If the refetch never lands
            // within the timeout, synthesize a current-period bar so the chart
            // resumes instead of freezing on flat lines.
            if (handler.pendingReload) {
                const fresh = handler.fullName ? LastBarsCache.get(handler.fullName) : undefined;
                if (fresh !== undefined) {
                    handler.lastDailyBar = fresh;
                    handler.pendingReload = false;
                } else if (handler.pendingReloadSince && Date.now() - handler.pendingReloadSince > PENDING_RELOAD_TIMEOUT_MS) {
                    handler.lastDailyBar = synthesizePostTimeoutBar(handler.resolution, tradeTime, tradePrice / divider, volume);
                    handler.pendingReload = false;
                } else {
                    return;
                }
            }
            const lastDailyBar: LastDailyBar = handler.lastDailyBar;
            const resolution: string = handler.resolution;
            // Index volume/OI proxy (ADVA-847): an index's own spot tick carries
            // volume=0 and no OI, so when this bar handler is linked to a
            // nearest-future proxy channel we substitute the future's latest
            // cumulative volume + OI (kept by the 'volume-proxy' handler). For a
            // normal scrip proxyChannel is undefined → effective* == the tick's own
            // values, leaving existing behaviour byte-for-byte unchanged.
            let effectiveVolume = volume;
            let effectiveOi: any = oi;
            if (handler.proxyChannel) {
                const p = proxyLatest.get(handler.proxyChannel);
                effectiveVolume = p ? p.cumVolume : 0;
                effectiveOi = p ? p.oi : undefined;
            }
            var nextDailyBarTime: number = 0
            // Keep the parsed `tradeTime` pristine for the 'volume-proxy' candle
            // bucket; bucket the bar against `barTradeTime` instead.
            var barTradeTime: number = tradeTime
            if (resolution == '1') {
                nextDailyBarTime = getNextMinBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            } else if (resolution == '1D') {
                barTradeTime = moment(tradeTime).startOf('day').toDate().getTime() + 19800000
                nextDailyBarTime = getNextDailyBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);

            } else if (resolution == '1W') {
                barTradeTime = moment(tradeTime).startOf('week').toDate().getTime() + 86400000 + 19800000 //week start from monday so adding 1 day more
                nextDailyBarTime = getNextWeeklyBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            }
            else if (resolution == '1M') {
                barTradeTime = moment(tradeTime).startOf('month').toDate().getTime() + 19800000
                nextDailyBarTime = getNextMonthBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            } else if (resolution == '1Y') {
                barTradeTime = moment(tradeTime).startOf('year').toDate().getTime() + 19800000
                nextDailyBarTime = getNextyearBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            } else if (resolution == '1S') {
                barTradeTime = moment(tradeTime).startOf('second').toDate().getTime()
                nextDailyBarTime = getNextSecBarTime((lastDailyBar !== null && lastDailyBar !== undefined) ? lastDailyBar.time : null);
            }
            let bar: LastDailyBar = {};
            if (barTradeTime >= nextDailyBarTime) {
                if (resolution == '1D') {
                    if (openPrice > 0 && highPrice > 0 && lowPrice > 0 && closePrice > 0 && tradePrice > 0) {
                        bar = {
                            time: nextDailyBarTime,
                            open: openPrice / divider,
                            high: highPrice / divider,
                            low: lowPrice / divider,
                            close: tradePrice / divider,
                            volume: effectiveVolume
                        };
                    } else {
                        bar = {
                            time: nextDailyBarTime,
                        }
                    }
                } else {
                    bar = {
                        time: nextDailyBarTime,
                        open: tradePrice / divider,
                        high: tradePrice / divider,
                        low: tradePrice / divider,
                        close: tradePrice / divider,
                        volume: effectiveVolume - Number(lastDailyBar != undefined ? lastDailyBar?.totalLastDayVolumeTillBar : 0),
                        totalLastDayVolumeTillBar: effectiveVolume
                    };
                }
            } else {
                if (resolution == '1D') {
                    bar = {
                        ...lastDailyBar,
                        high: highPrice > 0 ? highPrice / divider : lastDailyBar.high,
                        low: lowPrice > 0 ? lowPrice / divider : lastDailyBar.low,
                        open: openPrice > 0 ? openPrice / divider : lastDailyBar.open,
                        close: tradePrice / divider,
                        volume: effectiveVolume
                    };
                } else {
                    bar = {
                        ...lastDailyBar,
                        high: Math.max(Number(lastDailyBar ? lastDailyBar.high : 0), tradePrice / divider),
                        low: Math.min(Number(lastDailyBar ? lastDailyBar.low : 0), tradePrice / divider),
                        close: tradePrice / divider,
                        volume: effectiveVolume - Number(lastDailyBar?.totalLastDayVolumeTillBar),
                    };
                }
            }
            if (bar.volume && bar.volume < 0) {
                bar.volume = 0
                // console.log("negative vol", volume,lastDailyBar.totalLastDayVolumeTillBar )
            }
            // Index volume/OI proxy: the index bar's TIME + OHLC come from the index
            // spot tick (built above); its VOLUME comes from the future's
            // current-candle volume (`perCandle`), tracked from the future stream in
            // the 'volume-proxy' handler — so it equals the future's own chart volume
            // and does NOT depend on the index's stored history (which may be
            // unenriched / zero). OI carried likewise (rendering deferred). No-op for
            // non-proxy scrips.
            if (handler.proxyChannel) {
                const pl = proxyLatest.get(handler.proxyChannel);
                bar.volume = pl ? pl.perCandle : 0;
                bar.oi = effectiveOi;
            }
            handler.handler.callback(bar)

            handler.lastDailyBar = bar;
        } else if (handler.type == 'volume-proxy') {
            // Nearest-future tick feeding an index chart (ADVA-847). We compute the
            // future's volume WITHIN the current candle ("perCandle") directly from
            // its cumulative day volume, tracking the candle base ourselves —
            // exactly how the future's own chart derives volume. This is independent
            // of the index's stored history (enriched server-side, possibly zero
            // where the backend enrichment isn't running), so the index's live
            // volume matches the future's chart bar-for-bar.
            const bucket = proxyCandleStart(tradeTime, handler.resolution);
            let st = proxyState.get(channelString);
            if (!st || st.bucket !== bucket) {
                // New candle: base = this first tick's cumulative — mirrors the
                // future chart's own new-candle base (it excludes the candle's first
                // trade), so the index per-candle volume equals it exactly.
                st = { bucket: bucket, base: volume };
                proxyState.set(channelString, st);
            }
            let perCandle = volume - st.base;
            if (perCandle < 0) perCandle = 0;
            proxyLatest.set(channelString, { cumVolume: volume, perCandle: perCandle, oi: oi, oi_ch: oi_ch });
            // Safe re-emit: refresh ONLY the index's current bar (same candle), so
            // the volume pane advances between index spot ticks. We never create or
            // move a candle here — the index spot tick owns time + OHLC + rollover —
            // so if the future has already rolled into a new candle we skip and let
            // the next index tick build it (prevents a future tick painting the new
            // candle's volume onto the old bar).
            const idxItem = handler.indexChannel ? channelToSubscription.get(handler.indexChannel) : undefined;
            if (idxItem && idxItem.handlers) {
                idxItem.handlers.forEach((idxHandler: Handlers) => {
                    if (idxHandler.type !== 'bar') return;
                    if (idxHandler.id !== handler.id) return; // uid-matched index<->proxy pair
                    const lb = idxHandler.lastDailyBar;
                    if (!lb || lb.time !== bucket) return; // different candle → let the index tick roll it
                    const bar: LastDailyBar = { ...lb, volume: perCandle, oi: oi };
                    try {
                        idxHandler.handler.callback(bar);
                    } catch (e) {
                        console.info("volume-proxy re-emit failed", e);
                    }
                    idxHandler.lastDailyBar = bar;
                });
            }
        } else if (handler.type == 'single-quotes') {
            let quote = {
                s: 'success',
                n: handler.symbol,
                v: {
                    ch: (tradePrice - closePrice) / divider,
                    chp: ((tradePrice - closePrice) / closePrice * 100),
                    lp: tradePrice / divider,
                    open_price: openPrice / divider,
                    high_price: highPrice / divider,
                    low_price: lowPrice / divider,
                    prev_close_price: closePrice / divider,
                    volume: volume,
                    dpr: dpr,
                    ask: sell_price,
                    ask_qty: sell_qty,
                    bid: buy_price,
                    bid_qty: buy_qty,
                    average_trade_price: ((highPrice + lowPrice) / 2) / (divider)
                }

            }
            let mapper = singleQuoteMap.get(handler.id)
            if (mapper) {
                let all = true
                type MapperValue = {
                    quote: {
                        n: string;
                        v: Subscribed;
                    };
                };
                type Mapper = {
                    [key: string]: MapperValue;
                };
                let callbackResp = []
                mapper[market_segment + "-" + token].quote = quote
                for (const [key, value] of Object.entries(mapper)) {
                    const typedValue = value as MapperValue;
                    if (Object.keys(typedValue.quote).length == 0) {
                        all = false
                    } else {
                        callbackResp.push({
                            "s": "ok",
                            "n": typedValue.quote.n,
                            "v": {
                                "ch": typedValue.quote.v.ch,
                                "chp": typedValue.quote.v.chp,
                                "short_name": typedValue.quote.n.split(":")[1],
                                "exchange": typedValue.quote.n.split(":")[0],
                                "description": typedValue.quote.n,
                                "lp": typedValue.quote.v.lp,
                                "ask": typedValue.quote.v.ask || 0,
                                "bid": typedValue.quote.v.bid || 0,
                                "open_price": typedValue.quote.v.open_price,
                                "high_price": typedValue.quote.v.high_price,
                                "low_price": typedValue.quote.v.low_price,
                                "prev_close_price": typedValue.quote.v.prev_close_price,
                                "volume": typedValue.quote.v.volume,
                                "dpr": typedValue.quote.v.dpr
                            }
                        })
                    }
                }
                if (all) {
                    handler.handler.callback(callbackResp)
                    singleQuoteMap.delete(handler.handler.id)
                    unsubscribe(handler.handler.id)
                }
            }


        }
        else if (handler.type == 'depth') {
            depth['ch'] = (tradePrice - closePrice) / divider
            depth['chp'] = ((tradePrice - closePrice) / closePrice * 100)
            depth['openPrice'] = openPrice / divider
            depth['closePrice'] = closePrice / divider
            depth['highPrice'] = highPrice / divider
            depth['lowPrice'] = lowPrice / divider
            depth['volume'] = volume
            depth['lp'] = tradePrice / divider
            depth['totalBuyQty'] = totalBuyQty
            depth['totalSellQty'] = totalSellQty
            depth['LTQ'] = lastTradeQuantity
            handler.handler.callback(depth)
        }

    });
}

function getNextDailyBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 'd').startOf('day').toDate()
    return new_date.getTime() + 19800000;
}
function getNextWeeklyBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate();
        return new_date.getTime() + 19800000;
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 'W').startOf('isoWeek').toDate(); // ISO week starts on Monday
    return new_date.getTime() + 19800000;
}
function getNextMonthBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 'M').startOf('month').toDate()
    return new_date.getTime() + 19800000;
}

function getNextyearBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 'Y').startOf('year').toDate()
    return new_date.getTime() + 19800000;
}

function getNextSecBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    var new_date = moment(date).add('1', 's').startOf('second').toDate()
    return new_date.getTime();
}
function getNextMinBarTime(barTime: number | null | undefined) {
    if (barTime == null) {
        var new_date = moment().startOf('day').toDate()
        return new_date.getTime() + 19800000
    }
    const date = new Date(barTime);
    const curTime = moment();
    var duration = moment.duration(curTime.diff(date)).asMinutes();
    if (duration > 1) {
        return curTime.startOf('minute').toDate().getTime()
    } else {
        var new_date = moment(date).add('1', 'm').startOf('minute').toDate()
        return new_date.getTime();
    }
}

// Start-of-candle (ms) for a trade time at the given resolution. Matches the
// `barTradeTime` / `time` the bar-build branches assign per resolution, so the
// volume proxy's candle buckets line up with the index bar handler's candles.
// Used only by the 'volume-proxy' handler to detect future-candle rollover.
function proxyCandleStart(tradeTime: number, resolution: string): number {
    if (resolution == '1S') {
        return moment(tradeTime).startOf('second').toDate().getTime();
    } else if (resolution == '1D') {
        return moment(tradeTime).startOf('day').toDate().getTime() + 19800000;
    } else if (resolution == '1W') {
        return moment(tradeTime).startOf('week').toDate().getTime() + 86400000 + 19800000;
    } else if (resolution == '1M') {
        return moment(tradeTime).startOf('month').toDate().getTime() + 19800000;
    } else if (resolution == '1Y') {
        return moment(tradeTime).startOf('year').toDate().getTime() + 19800000;
    }
    // '1' and any intraday minute resolution bucket to the start of the minute.
    return moment(tradeTime).startOf('minute').toDate().getTime();
}

function odinDateParse(rand: string) {
    let [dateStr, timeStr] = rand.split(" ")
    let final_time = "T" + timeStr[0] + timeStr[1] + ":" + timeStr[2] + timeStr[3] + ":" + timeStr[4] + timeStr[5] + "+05:30"
    return Date.parse(dateStr + final_time)
}

//Return odin request parameters for depth Subscription
function _getSubscribeDepthRequest(symbol: ResolveSymbolInfo) {
    var request = "63=FT3.0|64=127|65=80|"
    if (symbol.odin_market_segment_id === undefined) {
        if (symbol.market_segment_id !== undefined) {
            symbol.odin_market_segment_id = symbol.market_segment_id
        }
    }
    if (symbol.odin_token === undefined) {
        symbol.odin_token = symbol.token
    }
    return request + "1=" + symbol.odin_market_segment_id + "|7=" + symbol.odin_token + "|230=1"
}

//Return odin request parameters for depth Unsubscription
function _getUnsubscribeDepthRequest(symbol: ResolveSymbolInfo) {
    var request = "63=FT3.0|64=206|65=80|"
    return request + "1=" + symbol.odin_market_segment_id + "|7=" + symbol.odin_token + "|230=2"
}

//Return odin request parameters for touchLine Unsubscription
function _getUnsubscribeTouchRequest(symbol: ResolveSymbolInfo) {
    var request = "63=FT3.0|64=127|65=80|"
    return request + "1=" + symbol.odin_market_segment_id + "&7=" + symbol.odin_token + "|230=2"
}

//Return odin request parameters for touchLine Subscription
function _getSubscribeTouchRequest(symbol: ResolveSymbolInfo) {
    if (symbol.odin_market_segment_id === undefined) {
        if (symbol.market_segment_id !== undefined) {
            symbol.odin_market_segment_id = symbol.market_segment_id
        }
    }
    if (symbol.odin_token === undefined) {
        symbol.odin_token = String(symbol.token)
    }
    return "63=FT3.0|64=206|65=80|1=" + symbol.odin_market_segment_id + "$7=" + symbol.odin_token + "|" + "4=1000|230=1"
}

function _subscribeDepth(symbol: ResolveSymbolInfo, onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string) {
    var channelString = symbol.odin_market_segment_id + "-" + symbol.odin_token
    _setChannelMap(symbol, channelString, onRealtimeCallback, subscribeUID, 'depth')
    // SendMessage(_getSubscribeDepthRequest(symbol))
}

function _subscribeQuotes(symbols: ResolveSymbolInfo[], onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string, type: string) {
    symbols.forEach((symbol: ResolveSymbolInfo) => {
        let channelString = symbol.odin_market_segment_id + "-" + symbol.odin_token
        _setChannelMap(symbol, channelString, onRealtimeCallback, subscribeUID, type)
    })
    // SendMessage(_getTouchRequest(symbols))
}

function _subscribeBars(symbols: ResolveSymbolInfo[], onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string, lastDailyBar: LastDailyBar, resolution: string) {
    symbols.forEach((symbol: ResolveSymbolInfo) => {
        let channelString = symbol.odin_market_segment_id + "-" + symbol.odin_token

        // Index volume/OI proxy (ADVA-847): an index spot feed carries no volume/OI,
        // so when symbol_info advertises a nearest-future proxy we ALSO subscribe
        // that future's channel and merge its volume/OI onto the index bar (see the
        // 'bar' and 'volume-proxy' handlers in ProcessPacketString).
        let proxyChannel: string | null = null;
        if (symbol.has_volume_proxy && symbol.volume_source_token && symbol.volume_source_market_segment_id) {
            proxyChannel = symbol.volume_source_market_segment_id + "-" + symbol.volume_source_token;
        }

        _setChannelMap(symbol, channelString, onRealtimeCallback, subscribeUID, 'bar', resolution, lastDailyBar, { proxyChannel })

        if (proxyChannel) {
            _setChannelMap(symbol, proxyChannel, onRealtimeCallback, subscribeUID, 'volume-proxy', resolution, lastDailyBar, { indexChannel: channelString })
            // The future channel isn't in `symbols`, so the socket-subscribe loop in
            // subscribeOnOdinStream won't cover it — subscribe it (touchline) here.
            const proxySym: ResolveSymbolInfo = {
                odin_market_segment_id: Number(symbol.volume_source_market_segment_id),
                odin_token: String(symbol.volume_source_token),
            };
            const proxySubs = channelToSubscription.get(proxyChannel);
            if (proxySubs && !proxySubs.isSubscribed) {
                SendMessage(_getSubscribeTouchRequest(proxySym));
                proxySubs.isSubscribed = true;
                proxySubs.isTouchline = true;
                channelToSubscription.set(proxyChannel, proxySubs);
            }
        }
    })
    // SendMessage(_getTouchRequest(symbols))
}

function _subscribeSingleQuotes(symbols: ResolveSymbolInfo[], onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string) {
    symbols.forEach((symbol: ResolveSymbolInfo) => {
        let channelString = symbol.odin_market_segment_id + "-" + symbol.odin_token
        let existing = singleQuoteMap.get(subscribeUID)
        if (existing) {
            existing[symbol.odin_market_segment_id + "-" + symbol.odin_token] = { quote: {} }
        } else {
            singleQuoteMap.set(subscribeUID, new Object())
            singleQuoteMap.get(subscribeUID)[symbol.odin_market_segment_id + "-" + symbol.odin_token] = { quote: {}, symbol: symbol.symbol }
        }
        _setChannelMap(symbol, channelString, onRealtimeCallback, subscribeUID, 'single-quotes')
    })
    // SendMessage(_getTouchRequest(symbols))
}
//Map the other requirements like id,resolution , type etc. if subscribeUID doesn't exist
function _setChannelMap(symbol: ResolveSymbolInfo, channelString: string, onRealtimeCallback: (a: Quotes[]) => void, subscribeUID: string, type: string, resolution = '', lastDailyBar: LastDailyBar = {}, opts: { proxyChannel?: string | null; indexChannel?: string } = {}) {
    let subscriptionItem = channelToSubscription.get(channelString);
    let handler = {
        id: subscribeUID,
        callback: onRealtimeCallback,
    };
    // proxyChannel: set on an index 'bar' handler → which future channel to read
    // volume/OI from. indexChannel: set on a 'volume-proxy' handler → which index
    // channel to feed. Both undefined for ordinary subscriptions (ADVA-847).
    const proxyChannel = (opts && opts.proxyChannel) || undefined;
    const indexChannel = (opts && opts.indexChannel) || undefined;
    const makeHandler = (): Handlers => ({ handler: handler, type: type, resolution: resolution, lastDailyBar: lastDailyBar, symbol: String(symbol.symbol), fullName: String(symbol.full_name || ""), id: subscribeUID, proxyChannel: proxyChannel, indexChannel: indexChannel });
    if (subscriptionItem) {
        // Match on id AND type: the same guid can hold a 'bar' on the index channel
        // and a 'volume-proxy' on the future channel — distinct rows.
        var index = subscriptionItem.handlers.findIndex((object: Handlers) => {
            return object.id == subscribeUID && object.type == type
        })
        if (index == -1) {
            subscriptionItem.handlers.push(makeHandler());
        } else {
            subscriptionItem.handlers[index] = makeHandler()
        }
    } else {
        subscriptionItem = {
            handlers: [makeHandler()],
            isSubscribed: false,
            isMarketDepth: false,
            isTouchline: false,
            lastPacket: ""
        };
    }
    channelToSubscription.set(channelString, subscriptionItem as OdinMessagePacket);
    // guid -> [channels]. A bar sub with a volume proxy spans two channels, so
    // track an array and append (deduped) rather than overwrite.
    let chans = guidToSubscription.get(subscribeUID);
    if (!Array.isArray(chans)) {
        chans = [];
        guidToSubscription.set(subscribeUID, chans);
    }
    if (chans.indexOf(channelString) === -1) {
        chans.push(channelString);
    }
}

//Change the market depth data in a particular format from Odin's response
function marketDepthProcessMessage(message: string) {
    var scrip = message.split("|");
    let quantity_a: string[] = []
    let price_a: string[] = []
    let order_a: string[] = []
    let depth: MarketDepth = {
        snapshot: true,
        asks: [],
        bids: []
    }
    let data_map = new Map()

    for (var i = 0, len = scrip.length; i < len; i++) {
        var data = scrip[i];
        var pos = data.indexOf('=');
        if (pos >= 0) {
            var key = data.substring(0, pos);
            var value = data.substring(pos + 1, scrip[i].length);
            if (key == '11') {
                var buySell = data.substring(pos + 1, scrip[i].length);
                //BuySell==1 means buy market depth 
                if (buySell == '1') {
                    for (var j = 0, len2 = 5; j < len2; j++) {
                        let marketDepthData = scrip[i + j + 1].split("$")
                        marketDepthData.map((d: string, index: number) => {
                            if (index == 0) {
                                quantity_a = d.split("=")
                            } else if (index == 1) {
                                price_a = d.split("=")
                            } else if (index == 2) {
                                order_a = d.split("=")
                            }
                        })
                        depth.bids?.push({
                            price: price_a.length > 1 ? parseFloat(price_a[1]) : 0,
                            volume: quantity_a.length > 1 ? parseInt(quantity_a[1]) : 0,
                            orders: order_a.length > 1 ? parseInt(order_a[1]) : 0
                        })
                    }
                } else {
                    for (var j = 0, len2 = 5; j < len2; j++) {
                        let marketDepthData = scrip[i + j + 1].split("$")
                        marketDepthData.map((d: string, index: number) => {
                            if (index == 0) {
                                quantity_a = d.split("=")
                            } else if (index == 1) {
                                price_a = d.split("=")
                            } else if (index == 2) {
                                order_a = d.split("=")
                            }
                        })
                        depth.asks?.push({
                            price: price_a.length > 1 ? parseFloat(price_a[1]) : 0,
                            volume: quantity_a.length > 1 ? parseInt(quantity_a[1]) : 0,
                            orders: order_a.length > 1 ? parseInt(order_a[1]) : 0
                        })
                    }

                }

            } else {
                data_map.set(key, value)
            }
            // processMarketDepthKeyValuePair(scripDetailModel, key, value);
        }
    }
    depth.asks?.sort((a: Bids, b: Bids) => (a.price > b.price) ? 1 : ((b.price > a.price) ? -1 : 0))
    depth.bids?.sort((a: Bids, b: Bids) => (a.price > b.price) ? 1 : ((b.price > a.price) ? -1 : 0))
    data_map.set("depth", depth)
    return data_map
}